/// <amd-module name="Test/TestPlansLibrary/LibraryHub/Component/TestPlansLibraryConnectedView" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import * as Reselect from "Reselect";
import * as Resources from "Test/TestPlansLibrary/LibraryHub/Resources";
import { TestPlansLibraryConstants, WorkItemConstants } from "../Constants";
import { ITestPlansLibraryAwareState } from "../Redux/TestPlansLibraryContracts";
import {
    initializeTestPlansLibraryLocalContext,
    testPlansLibraryLocalContextData,
    TestPlansLibraryLocalContext
} from "../Utils/TestPlansLibraryLocalContextProvider";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { addTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { ConnectedHeaderCommandBar } from "VSSUI/ReduxHeaderCommandBar";
import { Page } from "VSSUI/Page";
import { IVssContributedTab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { TestPlansLibraryActions } from "../Redux/TestPlansLibraryActions";

export interface ITestPlansLibraryProps extends IVssComponentProperties {
    /**
     * Currently selected tab
     */
    selectedTab: ObservableValue<string>;
}

export interface ITestPlansLibraryMappedProps {
    editColumnOptions(): void;
}

/**
 * TestPlansLibraryView: This is the core root view component wrapped by the portal component.
 * This view renders the different tabs in the library page.
 */
export class TestPlansLibraryView extends VssComponent<ITestPlansLibraryProps & ITestPlansLibraryMappedProps> {
    private _pivotProvider: IReadonlyObservableArray<IVssContributedTab>;

    constructor(props: ITestPlansLibraryProps & ITestPlansLibraryMappedProps, context: IVssComponentContext) {
        super(props, context);
        this._setContributedProvider();
        initializeTestPlansLibraryLocalContext(this.props.selectedTab.value);
    }

    public render(): JSX.Element {
        const selectedTab = this.props.selectedTab;
        return (
            <TestPlansLibraryLocalContext.Provider value={testPlansLibraryLocalContextData}>
                <Page className="testplans-library-hub-page" tabProviders={this._pivotProvider} selectedTabId={selectedTab}>
                    <div className="testplans-library-hub-container flex-grow">
                        <Header title={Resources.Library} titleSize={TitleSize.Medium}></Header>
                        <TabBar
                            className="testplans-library-tab-bar"
                            onSelectedTabChanged={this._onSelectedTabChanged}
                            tabSize={TabSize.Tall}
                            renderAdditionalContent={this._getPivotBarItems.bind(this)}
                        />
                        <TabContent />
                    </div>
                </Page>
            </TestPlansLibraryLocalContext.Provider>
        );
    }

    private _setContributedProvider() {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const routeValues = navigationService.getCurrentRoute().routeValues;
        const providerRouteValues = {
            project: routeValues["project"]
        };
        let id = "ms.vss-test-web.testplans-library:pivots";

        const tabsCollection = new ObservableCollection<IVssContributedTab>();
        // add the list of tabs testcases, shared steps etc.
        addTabs(this.context.pageContext, tabsCollection, id, { routeValues: providerRouteValues, updateUrlOnPivotChange: true });

        this._pivotProvider = tabsCollection;
    }

    /**
     * This is used to get additional items for tab bar eg. action buttons and filter button
     */
    private _getPivotBarItems(): JSX.Element {
        return (
            <div className="testplans-header-command-bar">
                <ConnectedHeaderCommandBar showFilter={true} commandsSelector={CommandSelector} viewActionsSelector={this._viewActions} />
            </div>
        );
    }

    private _onSelectedTabChanged = (pivotId: string) => {
        this.props.selectedTab.value = pivotId;
        testPlansLibraryLocalContextData.setCurrentPivot(pivotId);
    };

    private _createNewWorkItem = () => {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName: string = "";

        if (tfsPageData && tfsPageData.project) {
            projectName = tfsPageData.project.name;
        }

        const workItemUrl: string = routeUrl(this.context.pageContext, "ms.vss-test-web.testplans-workitem-page-route", {
            project: projectName,
            type: this.props.selectedTab.value,
            mode: WorkItemConstants.WorkItemCreateMode
        });

        FastPageSwitch(this.context.pageContext, workItemUrl, true);
    };

    private _viewActions = (): IHeaderCommandBarItem[] => {
        const tabBarCommands: IHeaderCommandBarItem[] = [];
        if (this.props.selectedTab.value === TestPlansLibraryConstants.TestCaseTabId) {
            tabBarCommands.push({
                ariaLabel: Resources.NewTestCase,
                id: TestPlansLibraryConstants.NewTestCaseButtonId,
                iconProps: {
                    iconName: "Add",
                    className: "header-command-bar-icons"
                },
                onActivate: () => {
                    this._createNewWorkItem();
                },
                subtle: true,
                className: "header-command-bar-actions",
                text: Resources.NewTestCase,
                important: true
            });
        }
        tabBarCommands.push(
            {
                ariaLabel: Resources.Import,
                id: TestPlansLibraryConstants.ImportButtonId,
                iconProps: {
                    iconName: "Upload",
                    className: "header-command-bar-icons"
                },
                subtle: true,
                className: "header-command-bar-actions",
                text: Resources.Import,
                important: true
            },
            {
                ariaLabel: Resources.Export,
                id: TestPlansLibraryConstants.ExportButtonId,
                iconProps: {
                    iconName: "Download",
                    className: "header-command-bar-icons"
                },
                subtle: true,
                className: "header-command-bar-actions",
                text: Resources.Export,
                important: true
            },
            {
                ariaLabel: Resources.ColumnOptions,
                id: TestPlansLibraryConstants.ColumnOptionsButtonId,
                iconProps: {
                    iconName: "Repair",
                    className: "header-command-bar-icons"
                },
                subtle: true,
                className: "header-command-bar-actions",
                onActivate: () => {
                    this.props.editColumnOptions();
                },
                text: Resources.ColumnOptions,
                important: true
            }
        );
        return tabBarCommands;
    };
}

export const CommandSelector = Reselect.createSelector(
    // Just a placeholder for now; the ConnectedHeaderCommandBar requires this but we don't have any commands
    [() => {}],
    () => {
        return [];
    }
);

function mapStateToProps(state: ITestPlansLibraryAwareState, props: ITestPlansLibraryProps): Partial<ITestPlansLibraryMappedProps> {
    return {};
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestPlansLibraryMappedProps> {
    return {
        editColumnOptions: () =>
            dispatch(TestPlansLibraryActions.editColumnOptionFieldsClicked()),
    };
}

export function TestCasesTabFunctionalComponent(props: ITestPlansLibraryProps & ITestPlansLibraryMappedProps): JSX.Element {
    const testPlansLibraryLocalContext = React.useContext(TestPlansLibraryLocalContext);

    return <TestPlansLibraryView {...props} testPlansLibraryContext={testPlansLibraryLocalContext} />;
}

export const TestPlansLibraryConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(TestCasesTabFunctionalComponent);
